import { cn } from "@/lib/utils";
import { formatCurrencyCompact, formatPercent } from "@/lib/format";

export interface AllocationSegment {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface AllocationChartProps {
  segments: AllocationSegment[];
  centerLabel: string;
  centerValue: string;
  liabilities?: { label: string; value: number } | null;
  className?: string;
}

const RADIUS = 60;
const STROKE = 18;
const CIRC = 2 * Math.PI * RADIUS;

export function AllocationChart({
  segments,
  centerLabel,
  centerValue,
  liabilities,
  className,
}: AllocationChartProps) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const hasData = total > 0;

  let offset = 0;
  const arcs = hasData
    ? segments
        .filter((s) => s.value > 0)
        .map((seg) => {
          const length = (seg.value / total) * CIRC;
          const dashArray = `${length} ${CIRC - length}`;
          const dashOffset = -offset;
          offset += length;
          return { ...seg, dashArray, dashOffset };
        })
    : [];

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
        <div className="relative h-40 w-40 shrink-0">
          <svg
            viewBox="0 0 160 160"
            className="h-full w-full -rotate-90"
            aria-hidden
          >
            <circle
              cx={80}
              cy={80}
              r={RADIUS}
              fill="none"
              stroke="hsl(var(--muted))"
              strokeWidth={STROKE}
            />
            {arcs.map((arc) => (
              <circle
                key={arc.key}
                cx={80}
                cy={80}
                r={RADIUS}
                fill="none"
                stroke={arc.color}
                strokeWidth={STROKE}
                strokeDasharray={arc.dashArray}
                strokeDashoffset={arc.dashOffset}
                strokeLinecap="butt"
              />
            ))}
          </svg>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {centerLabel}
            </span>
            <span className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-foreground">
              {centerValue}
            </span>
          </div>
        </div>

        <ul className="flex-1 space-y-2 text-sm">
          {segments.map((seg) => {
            const pct = hasData ? (seg.value / total) * 100 : 0;
            return (
              <li key={seg.key} className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: seg.color }}
                  />
                  <span className="truncate text-foreground">{seg.label}</span>
                </span>
                <span className="flex items-baseline gap-2 font-mono tabular-nums text-muted-foreground">
                  <span>{formatCurrencyCompact(seg.value)}</span>
                  <span className="text-[11px]">{formatPercent(pct, 1)}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {liabilities ? (
        <div className="flex items-center justify-between border-t border-border pt-4 text-sm">
          <span className="flex items-center gap-2">
            <span
              aria-hidden
              className="h-2.5 w-2.5 shrink-0 rounded-sm bg-destructive"
            />
            <span className="text-foreground">{liabilities.label}</span>
            <span className="text-xs text-muted-foreground">(shown separately)</span>
          </span>
          <span className="font-mono tabular-nums text-muted-foreground">
            {formatCurrencyCompact(liabilities.value)}
          </span>
        </div>
      ) : null}
    </div>
  );
}
